import { useState, useEffect, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const contactInfo = [
  {
    icon: "ri-whatsapp-line",
    label: "WhatsApp",
    value: "1-845-370-1756",
    href: "https://wa.me/18453701756",
  },
  {
    icon: "ri-mail-line",
    label: "Email",
    value: "Mswashington@thelittyagency",
    href: "mailto:Mswashington@thelittyagency",
  },
  {
    icon: "ri-map-pin-line",
    label: "Location",
    value: "United States",
    href: "#",
  },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact The LittyCity Agency LLC",
  url: `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
  description:
    "Get in touch with The LittyCity Agency LLC. Whether you are a creator, brand, or partner, reach out via WhatsApp or email and we will respond within 24 hours.",
  mainEntity: {
    "@type": "Organization",
    name: "The LittyCity Agency LLC",
    telephone: "+1-845-370-1756",
    email: "Mswashington@thelittyagency",
    areaServed: "US",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-845-370-1756",
      contactType: "customer service",
      availableLanguage: "English",
    },
  },
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const id = "schema-contact";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    document.title = "Contact Us | The LittyCity Agency LLC";

    return () => {
      observer.disconnect();
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (formData.message.length > 500) {
      setError("Message must be 500 characters or less.");
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d744vdmcsbv12mkucvog", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black">
      <Navbar />

      {/* Header */}
      <div className="pt-36 pb-20 bg-brand-black text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-gold/4 blur-3xl" />
        </div>
        <div className="relative z-10">
          <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-5">
            Let&apos;s Connect
          </span>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl leading-none mb-6">
            Get in <span className="text-gradient-gold">Touch</span>
          </h1>
          <p className="text-white/50 font-sans text-lg max-w-xl mx-auto">
            Whether you&apos;re a creator, brand, or partner — we&apos;d love to hear from you.
          </p>
          <div className="w-24 h-px bg-gold mx-auto mt-8" />
        </div>
      </div>

      <main>
        <section className="py-24 md:py-36 bg-brand-black">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Info */}
              <div className="reveal flex flex-col gap-6">
                <h2 className="font-display font-bold text-white text-2xl mb-2">
                  Contact <span className="text-gradient-gold">Information</span>
                </h2>
                <p className="text-white/50 font-sans text-sm leading-relaxed">
                  Reach out directly via WhatsApp or email. We typically respond
                  within 24 hours.
                </p>

                {contactInfo.map((info) => (
                  <a
                    key={info.label}
                    href={info.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-gold/30 bg-gold/5 group-hover:bg-gold/15 transition-colors duration-300 flex-shrink-0">
                      <i className={`${info.icon} text-gold text-xl`} />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs font-sans uppercase tracking-widest mb-0.5">
                        {info.label}
                      </p>
                      <p className="text-white font-sans text-sm font-medium group-hover:text-gold transition-colors duration-300">
                        {info.value}
                      </p>
                    </div>
                  </a>
                ))}

                {/* Decorative image */}
                <div className="mt-4 rounded-2xl overflow-hidden border border-gold/15">
                  <div className="w-full h-48">
                    <img
                      src="https://public.readdy.ai/ai/img_res/04653bb2fc8732bee60cc36583f11a23.jpg"
                      alt="LittyCity Agency"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-2 reveal reveal-delay-1">
                {submitted ? (
                  <div className="text-center card-gold bg-brand-card rounded-3xl p-16 h-full flex flex-col items-center justify-center">
                    <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-gold/10 border border-gold/30 mb-6">
                      <i className="ri-check-line text-gold text-4xl" />
                    </div>
                    <h3 className="font-display font-bold text-white text-3xl mb-4">
                      Message Sent!
                    </h3>
                    <p className="text-white/60 font-sans text-lg max-w-sm">
                      Thank you for reaching out. We&apos;ll get back to you
                      as soon as possible.
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="card-gold bg-brand-card rounded-3xl p-8 md:p-12 flex flex-col gap-6"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                          className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                        />
                      </div>
                      <div>
                        <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm focus:outline-none focus:border-gold/60 transition-colors duration-300"
                      >
                        <option value="" disabled>
                          What can we help you with?
                        </option>
                        <option value="Become a BIGO Host">Become a BIGO Host</option>
                        <option value="Agency Services">Agency Services</option>
                        <option value="Brand Partnership">Brand Partnership</option>
                        <option value="Platform Partnership">Platform Partnership</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-gold text-xs font-sans font-semibold tracking-widest uppercase block mb-2">
                        Message * (max 500 chars)
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us about yourself or your brand..."
                        className="w-full bg-brand-black border border-gold/20 rounded-xl px-4 py-3 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors duration-300 resize-none"
                      />
                      <p className="text-white/30 text-xs font-sans mt-1 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>

                    {error && (
                      <p className="text-red-400 text-sm font-sans">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-gold w-full py-4 rounded-xl text-white font-sans font-bold text-sm tracking-widest cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <i className="ri-send-plane-line" />
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
